import React, { useEffect, useRef, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import PlynoteHome from './pages/plynoteHome';
import TestPage from './pages/testpage';
import NoteEditorView from './pages/NoteViews/noteEditorView';
import NoteAdaptedView from './pages/NoteViews/noteAdaptedView';
import SideMenu from './pages/SideMenu';
import ConnectionPage from './pages/connectionPage';
import HomeLandingPage from './pages/landingPage/HomeLandingPage';
import PrivacyPolicy from './pages/landingPage/footer/PrivacyPolicy';
import TermsOfService from './pages/landingPage/footer/TermsOfService';
import SupportPage from './pages/landingPage/footer/SupportPage';
import PlynoteVsNotebookLM from './pages/landingPage/comparative/plynote_vs_notebooklm';
import PlynoteVsTurboAI from './pages/landingPage/comparative/plynote_vs_turbo_ai';
import PlynoteVsStudley from './pages/landingPage/comparative/plynote_vs_studley';
import MyNotes from './pages/MyNotes';
import SettingsPage from './pages/settingsPage';
import PayementOptionPopup from './pages/payementOptionPopup';
import BrandLogo from './components/ui/BrandLogo';
import { SideMenuProvider } from './context/sideMenuContext';
import { ColorProvider } from './context/colorContext';
import { useColorContext } from './context/colorContext';
import { NoteProvider } from './context/noteContexts/noteContext';
import { NavigateProvider } from './context/NavigateContext';
import { UserProvider, useUserContext } from './context/userContext';
import { UserLimitProvider, useUserLimitContext } from './context/user_limit_context';
import { LanguageProvider, useLanguageContext } from './context/languageContext';
import { loadUserSettings, saveTheme, saveLanguage } from './services/api/userSettingsService';
import './stylecss/App.css';

const VALID_THEME_TYPES = new Set(['clear', 'dark', 'system']);

const normalizeThemeType = (value) => {
	const normalized = String(value || '').trim().toLowerCase();
	return VALID_THEME_TYPES.has(normalized) ? normalized : null;
};

const APP_SAVE_OVERLAY_STYLE_ID = 'plynote-app-save-overlay-anim';
if (typeof document !== 'undefined') {
	const existingStyleEl = document.getElementById(APP_SAVE_OVERLAY_STYLE_ID);
	const spinnerKeyframes = `
		@keyframes appSaveSpinnerSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
		@keyframes appSaveOverlayIn { from { opacity: 0; } to { opacity: 1; } }
	`;

	if (!existingStyleEl) {
		const styleEl = document.createElement('style');
		styleEl.id = APP_SAVE_OVERLAY_STYLE_ID;
		styleEl.textContent = spinnerKeyframes;
		document.head.appendChild(styleEl);
	} else if (!String(existingStyleEl.textContent || '').includes('@keyframes appSaveSpinnerSpin')) {
		existingStyleEl.textContent = `${existingStyleEl.textContent || ''}\n${spinnerKeyframes}`;
	}
}

const isPasswordRecoveryLocation = (location) => {
	const search = String(location?.search || '');
	const searchParams = new URLSearchParams(search);
	const hash = String(location?.hash || '');
	return searchParams.get('mode') === 'reset'
		|| searchParams.get('type') === 'recovery'
		|| searchParams.has('token_hash')
		|| hash.includes('type=recovery');
};

const AuthBootstrapScreen = () => {
	const { bgPrimary, bgSecondary, textSecondary } = useColorContext();

	const safePrimary = String(bgPrimary || '').trim() || '#0A0A0F';
	const safeSecondary = String(bgSecondary || '').trim() || safePrimary;
	const safeText = String(textSecondary || '').trim() || '#A1A1AA';

	return (
		<div
			style={{
				width: '100vw',
				height: '100vh',
				display: 'grid',
				placeItems: 'center',
				background: `linear-gradient(180deg, ${safePrimary} 0%, ${safeSecondary} 100%)`,
				color: safeText,
				fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
				fontSize: '0.95rem',
			}}
		>
			<div>Loading your workspace...</div>
		</div>
	);
};

const MobileDesktopNotice = () => {
	const { bgPrimary, bgSecondary, textPrimary, textSecondary, activeAccent, resolvedThemeType } = useColorContext();

	const safePrimary = String(bgPrimary || '').trim() || '#0A0A0F';
	const safeSecondary = String(bgSecondary || '').trim() || safePrimary;
	const safeText = String(textSecondary || '').trim() || '#A1A1AA';
	const safeMainText = String(textPrimary || '').trim() || '#FFFFFF';
	const safeAccent = String(activeAccent || '').trim() || '#7C3AED';
	const isClearTheme = String(resolvedThemeType || '').toLowerCase() === 'clear';
	const shellBackground = isClearTheme
		? `linear-gradient(135deg, ${safePrimary} 0%, ${safeSecondary} 55%, ${safePrimary} 100%)`
		: `linear-gradient(135deg, ${safePrimary} 0%, ${safeSecondary} 48%, ${safePrimary} 100%)`;
	const cardBackground = isClearTheme
		? 'linear-gradient(180deg, rgba(255, 255, 255, 0.88) 0%, rgba(245, 245, 245, 0.94) 100%)'
		: 'linear-gradient(180deg, rgba(18, 18, 24, 0.88) 0%, rgba(10, 10, 15, 0.94) 100%)';
	const cardBorder = isClearTheme ? 'rgba(17, 17, 17, 0.08)' : `${safeAccent}26`;
	const badgeBackground = isClearTheme ? 'rgba(17, 17, 17, 0.04)' : 'rgba(255, 255, 255, 0.03)';
	const badgeText = isClearTheme ? safeMainText : safeText;

	return (
		<div
			style={{
				width: '100vw',
				height: '100vh',
				display: 'grid',
				placeItems: 'center',
				padding: '24px',
				background: shellBackground,
				color: safeText,
				fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
			}}
		>
			<div style={{ position: 'relative', width: '100%', maxWidth: '460px' }}>
				<div style={{ position: 'absolute', inset: '-18% 8% auto', height: 160, borderRadius: 999, background: `radial-gradient(circle, ${safeAccent}22 0%, transparent 68%)`, filter: 'blur(12px)', pointerEvents: 'none' }} />
				<div
					style={{
						position: 'relative',
						padding: '32px 26px 28px',
						borderRadius: '28px',
						background: cardBackground,
						backdropFilter: 'blur(18px)',
						border: `1px solid ${cardBorder}`,
						boxShadow: isClearTheme ? '0 24px 80px rgba(17, 17, 17, 0.10)' : '0 24px 80px rgba(0, 0, 0, 0.42)',
						textAlign: 'center',
					}}
				>
					<div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
						<BrandLogo size={28} style={{ letterSpacing: 1 }} />
					</div>
					<div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 18, padding: '7px 12px', borderRadius: 999, border: `1px solid ${isClearTheme ? 'rgba(17, 17, 17, 0.10)' : `${safeAccent}2A`}`, background: badgeBackground, color: badgeText, fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
						Version bureau recommandée
					</div>
					<div style={{ color: safeMainText, fontSize: 'clamp(1.45rem, 4vw, 2rem)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.08 }}>
						Connecté, mais l’expérience complète est sur ordinateur
					</div>
					<p style={{ margin: '16px auto 0', maxWidth: 360, fontSize: '1rem', lineHeight: 1.7, color: safeText }}>
						Plynote a été conçu pour un écran plus large. Pour naviguer dans le dashboard, gérer tes notes et profiter de l’interface complète, ouvre le site sur un ordinateur ou une tablette.
					</p>
					
				</div>
			</div>
		</div>
	);
};

const ProtectedRoute = ({ children }) => {
	const { is_authenticated, is_loading, is_recovery_session } = useUserContext();
	const location = useLocation();
	const isPasswordRecovery = isPasswordRecoveryLocation(location);

	if (is_loading) {
		return <AuthBootstrapScreen />;
	}

	if (isPasswordRecovery || is_recovery_session) {
		return <Navigate to="/connection?mode=reset" replace />;
	}

	if (!is_authenticated) {
		return <Navigate to="/connection" state={{ from: location }} replace />;
	}

	return children;
};

const AppSaveOverlay = () => null;

const LanguageSettingsSync = () => {
	const { is_authenticated, access_token } = useUserContext();
	const { language, applyServerLanguage } = useLanguageContext();
	const readyToSaveRef = useRef(false);
	const loadKeyRef = useRef(null);
	const lastSavedLangRef = useRef(null);

	useEffect(() => {
		if (!is_authenticated || !access_token) {
			readyToSaveRef.current = false;
			lastSavedLangRef.current = null;
			loadKeyRef.current = null;
			return;
		}

		const loadKey = Symbol();
		loadKeyRef.current = loadKey;
		readyToSaveRef.current = false;

		loadUserSettings(access_token)
			.then(({ language: savedLang }) => {
				if (loadKeyRef.current !== loadKey) return;
				if (savedLang) {
					lastSavedLangRef.current = savedLang;
					applyServerLanguage(savedLang);
				}
			})
			.catch(() => {})
			.finally(() => {
				if (loadKeyRef.current === loadKey) {
					readyToSaveRef.current = true;
				}
			});

		return () => { loadKeyRef.current = null; };
	}, [is_authenticated, access_token, applyServerLanguage]);

	useEffect(() => {
		if (!is_authenticated || !access_token || !readyToSaveRef.current) return;
		if (!language || lastSavedLangRef.current === language) return;

		lastSavedLangRef.current = language;
		saveLanguage(access_token, language).catch(() => {});
	}, [is_authenticated, access_token, language]);

	return null;
};

function App() {
	return (
		<HelmetProvider>
		<ColorProvider>
		<LanguageProvider>
			<Router>
				<UserProvider>
					<UserLimitProvider>
						<NoteProvider>
							<SideMenuProvider>
								<NavigateProvider>
									<AppShell />
								</NavigateProvider>
							</SideMenuProvider>
						</NoteProvider>
					</UserLimitProvider>
				</UserProvider>
			</Router>
		</LanguageProvider>
		</ColorProvider>
		</HelmetProvider>
	);
}

const PaiementRedirect = () => {
	const location = useLocation();
	return <Navigate to={`/settings${location.search}`} replace />;
};

const ThemeSettingsSync = () => {
	const { is_authenticated, access_token } = useUserContext();
	const { theme_type, setTheme_type } = useColorContext();
	const readyToSaveRef = useRef(false);
	const loadKeyRef = useRef(null);
	const lastSavedThemeRef = useRef(null);

	// Load theme from backend when the user authenticates.
	useEffect(() => {
		if (!is_authenticated || !access_token) {
			readyToSaveRef.current = false;
			lastSavedThemeRef.current = null;
			loadKeyRef.current = null;
			return;
		}

		const loadKey = Symbol();
		loadKeyRef.current = loadKey;
		readyToSaveRef.current = false;

		loadUserSettings(access_token)
			.then(({ theme }) => {
				if (loadKeyRef.current !== loadKey) return;
				const dbTheme = normalizeThemeType(theme);
				if (dbTheme) {
					lastSavedThemeRef.current = dbTheme;
					setTheme_type(dbTheme);
				}
			})
			.catch((err) => {
				if (loadKeyRef.current === loadKey) {
					console.error('[ThemeSync] Failed to load theme:', err.message);
				}
			})
			.finally(() => {
				if (loadKeyRef.current === loadKey) {
					readyToSaveRef.current = true;
				}
			});

		return () => {
			loadKeyRef.current = null;
		};
	}, [is_authenticated, access_token, setTheme_type]);

	// Save theme to backend when the user changes it.
	useEffect(() => {
		if (!is_authenticated || !access_token || !readyToSaveRef.current) {
			return;
		}

		const normalizedTheme = normalizeThemeType(theme_type);
		if (!normalizedTheme || lastSavedThemeRef.current === normalizedTheme) {
			return;
		}

		lastSavedThemeRef.current = normalizedTheme;

		saveTheme(access_token, normalizedTheme)
			.catch((err) => console.error('[ThemeSync] Failed to save theme:', err.message));
	}, [is_authenticated, access_token, theme_type]);

	return null;
};

const AppShell = () => {
	const { is_authenticated, is_recovery_session, is_loading } = useUserContext();
	const { upgradePopupOpen, upgradePopupMessage, closeUpgradePopup, currentPlanKey } = useUserLimitContext();
	const location = useLocation();
	const isPasswordRecovery = isPasswordRecoveryLocation(location);
	const isAuthenticatedForApp = is_authenticated && !isPasswordRecovery && !is_recovery_session;

	const [isMobileLayout, setIsMobileLayout] = useState(window.innerWidth <= 768);
	useEffect(() => {
		const handler = () => setIsMobileLayout(window.innerWidth <= 768);
		window.addEventListener('resize', handler);
		return () => window.removeEventListener('resize', handler);
	}, []);

	// Preserve the ?plan= param through the connection redirect so the user lands on the right Stripe page.
	// Also restore the original page when ProtectedRoute sent the user here with state.from.
	const connectionFrom = location.state?.from;
	const connectionPlanParam = new URLSearchParams(location.search || '').get('plan');
	const connectionRedirectTo = (() => {
		if (connectionPlanParam && connectionPlanParam !== 'free') {
			return `/settings?plan=${encodeURIComponent(connectionPlanParam)}`;
		}
		if (connectionFrom?.pathname && connectionFrom.pathname !== '/connection') {
			return `${connectionFrom.pathname}${connectionFrom.search || ''}${connectionFrom.hash || ''}`;
		}
		return '/';
	})();
	const shouldShowMobileDesktopNotice = isAuthenticatedForApp && isMobileLayout && location.pathname === '/';

	if (shouldShowMobileDesktopNotice) {
		return <MobileDesktopNotice />;
	}

	return (
		<div style={{ display: 'flex', flexDirection: isMobileLayout ? 'column' : 'row', width: '100vw', height: '100vh', overflow: 'hidden' }}>
			<ThemeSettingsSync />
			<LanguageSettingsSync />
			{isAuthenticatedForApp ? <SideMenu /> : null}
			<div style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto' }}>
				<Routes>
					<Route path="/" element={isAuthenticatedForApp ? <PlynoteHome /> : (is_loading ? <AuthBootstrapScreen /> : <HomeLandingPage />)} />
					<Route path="/privacy-policy" element={<PrivacyPolicy />} />
					<Route path="/terms-of-service" element={<TermsOfService />} />
					<Route path="/support" element={<SupportPage />} />
					{/* Redirect preserves the ?plan= param so the user lands directly on the Stripe checkout after auth */}
					<Route path="/comparative/plynote-vs-notebooklm" element={<PlynoteVsNotebookLM />} />
					<Route path="/comparative/plynote-vs-turbo-ai" element={<PlynoteVsTurboAI />} />
					<Route path="/comparative/plynote-vs-studley" element={<PlynoteVsStudley />} />
					<Route path="/connection" element={is_authenticated && !isPasswordRecovery ? <Navigate to={connectionRedirectTo} replace /> : <ConnectionPage />} />
					<Route path="/my-notes" element={<ProtectedRoute><MyNotes /></ProtectedRoute>} />
					<Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
					<Route path="/test" element={<ProtectedRoute><TestPage /></ProtectedRoute>} />
					<Route path="/note-editor" element={<ProtectedRoute><NoteEditorView /></ProtectedRoute>} />
					<Route path="/note-editor/:id" element={<ProtectedRoute><NoteEditorView /></ProtectedRoute>} />
					<Route path="/note-adapted" element={<ProtectedRoute><NoteAdaptedView /></ProtectedRoute>} />
					<Route path="/note-adapted/:id" element={<ProtectedRoute><NoteAdaptedView /></ProtectedRoute>} />
					<Route path="/note-adapted/:id/:type" element={<ProtectedRoute><NoteAdaptedView /></ProtectedRoute>} />
					{/* Backward-compat redirect: Stripe may still return to /paiement — forward to /settings preserving query params */}
					<Route path="/paiement" element={<PaiementRedirect />} />
					<Route path="*" element={<Navigate to="/" replace />} />
				</Routes>
				<PayementOptionPopup
					isOpen={upgradePopupOpen}
					onClose={closeUpgradePopup}
					limitMessage={upgradePopupMessage}
					currentPlanKey={currentPlanKey}
				/>
			</div>
			{isAuthenticatedForApp ? <AppSaveOverlay /> : null}
		</div>
	);
};

export default App;

